// Megabonk-style powerup pool. On level-up the server rolls 3 cards via
// rarity weights, the client shows them, the player picks one, and both
// sides converge on the chosen id. Effects are declarative: the server
// applies combat stats (hp, damage, absorption, xp mult), the client
// applies movement feel (speed mult) locally since the controller is
// client-authoritative.

import type { Role } from '../net/protocol';

export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';
export type PowerupCategory = 'stat' | 'body' | 'equipment';

export type PowerupEffect = {
  hpMaxAdd?: number;
  damageMult?: number;
  absorptionMult?: number;
  xpMult?: number;
  speedMult?: number;
  circleRadiusMult?: number;   // chad only
  ringOuterMult?: number;      // virgin only
  hitboxShrink?: number;       // virgin body mod — 0..1 (0.2 = 20% smaller)
  // Crit — additive chance to multiply a damage tick by critMult.
  critRate?: number;           // 0..1 additive; hard-capped at 0.95
  critMult?: number;           // multiplicative on top of base dmg; default 1.5
  // Dodge — additive chance to take 0 damage this tick.
  dodgeRate?: number;          // 0..1 additive; hard-capped at 0.6
};

export type Powerup = {
  id: string;
  name: string;
  blurb: string;
  rarity: Rarity;
  category: PowerupCategory;
  faction?: Role;              // if set, only appears for that faction
  oneTime?: boolean;           // body mods — cannot stack
  effect: PowerupEffect;
};

// Every powerup is now oneTime — duplicates are filtered out on
// roll + granted. Effects from DIFFERENT powerups still stack
// multiplicatively (e.g. protein × pre-workout × tumbler combine
// damageMult), so the progression system still rewards picking up
// variety. Body mods (radius/hitbox) were already oneTime.
export const POWERUPS: ReadonlyArray<Powerup> = [
  // ---- Common (55%) ----
  { id: 'energizer',   name: 'Energizer',        blurb: '+10% Movement Speed',         rarity: 'common',    category: 'stat', oneTime: true, effect: { speedMult: 1.10 } },
  { id: 'gym_bro',     name: 'Gym Bro',          blurb: '+10 Max HP',              rarity: 'common',    category: 'stat', oneTime: true, effect: { hpMaxAdd: 10 } },
  { id: 'protein',     name: 'Protein Shake',    blurb: '+8% Damage',              rarity: 'common',    category: 'stat', oneTime: true, effect: { damageMult: 1.08 } },
  { id: 'late_night',  name: 'Late Night Grind', blurb: '+10% XP gain',            rarity: 'common',    category: 'stat', oneTime: true, effect: { xpMult: 1.10 } },
  { id: 'caffeine',    name: 'Caffeine',         blurb: '+8% Movement Speed, +5% XP',  rarity: 'common',    category: 'stat', oneTime: true, effect: { speedMult: 1.08, xpMult: 1.05 } },
  { id: 'meal_prep',   name: 'Meal Prep',        blurb: '+15 Max HP',              rarity: 'common',    category: 'stat', oneTime: true, effect: { hpMaxAdd: 15 } },
  { id: 'finger_guns', name: 'Finger Guns',      blurb: '+10% NPC Absorption',     rarity: 'common',    category: 'stat', oneTime: true, effect: { absorptionMult: 1.10 } },
  { id: 'lucky_strike',name: 'Lucky Strike',     blurb: '+8% Crit Rate',           rarity: 'common',    category: 'stat', oneTime: true, effect: { critRate: 0.08 } },

  // ---- Uncommon (25%) ----
  { id: 'podcast',       name: 'Podcast',          blurb: '+20% XP gain',          rarity: 'uncommon',  category: 'stat', oneTime: true, effect: { xpMult: 1.20 } },
  { id: 'deep_breath',   name: 'Deep Breath',      blurb: '+25 Max HP',            rarity: 'uncommon',  category: 'stat', oneTime: true, effect: { hpMaxAdd: 25 } },
  { id: 'zen_garden',    name: 'Zen Garden',       blurb: '+18% NPC Absorption',   rarity: 'uncommon',  category: 'stat', oneTime: true, effect: { absorptionMult: 1.18 } },
  { id: 'pre_workout',   name: 'Pre-Workout',      blurb: '+15% Damage',           rarity: 'uncommon',  category: 'stat', oneTime: true, effect: { damageMult: 1.15 } },
  { id: 'noise_cancel',  name: 'Noise Cancelling', blurb: '+18% Movement Speed',       rarity: 'uncommon',  category: 'stat', oneTime: true, effect: { speedMult: 1.18 } },
  { id: 'lean_build',    name: 'Lean Build',       blurb: 'Smaller hitbox',        rarity: 'uncommon',  category: 'body', faction: 'int', oneTime: true, effect: { hitboxShrink: 0.20 } },
  { id: 'big_energy',    name: 'Big Energy',       blurb: '+25% Circle radius',    rarity: 'uncommon',  category: 'body', faction: 'ext', oneTime: true, effect: { circleRadiusMult: 1.25 } },
  { id: 'glass_cannon',  name: 'Glass Cannon',     blurb: '+15% Crit Rate, Crit ×1.8', rarity: 'uncommon', category: 'stat', oneTime: true, effect: { critRate: 0.15, critMult: 1.8 } },
  { id: 'sidestepper',   name: 'Sidestepper',      blurb: '+12% Dodge Rate',       rarity: 'uncommon',  category: 'stat', oneTime: true, effect: { dodgeRate: 0.12 } },

  // ---- Rare (12%) ----
  { id: 'pro_gamer',     name: 'Pro Gamer',        blurb: '+25% NPC Absorption, +5% Movement Speed', rarity: 'rare',      category: 'stat', oneTime: true, effect: { absorptionMult: 1.25, speedMult: 1.05 } },
  { id: 'lifetime_sub',  name: 'Lifetime Sub',     blurb: '+40 Max HP',            rarity: 'rare',      category: 'stat', oneTime: true, effect: { hpMaxAdd: 40 } },
  { id: 'tumbler',       name: 'Insulated Tumbler',blurb: '+25% Damage',           rarity: 'rare',      category: 'stat', oneTime: true, effect: { damageMult: 1.25 } },
  { id: 'sprinter',      name: "Sprinter's Legs",  blurb: '+30% Movement Speed',       rarity: 'rare',      category: 'stat', oneTime: true, effect: { speedMult: 1.30 } },
  { id: 'extended_ring', name: 'Extended Ring',    blurb: '+20% Outer ring',       rarity: 'rare',      category: 'body', faction: 'int', oneTime: true, effect: { ringOuterMult: 1.20 } },
  { id: 'mega_circle',   name: 'Mega Circle',      blurb: '+40% Circle radius',    rarity: 'rare',      category: 'body', faction: 'ext', oneTime: true, effect: { circleRadiusMult: 1.40 } },
  { id: 'headshot',      name: 'Headshot',         blurb: '+20% Crit Rate, +10% Damage', rarity: 'rare',  category: 'stat', oneTime: true, effect: { critRate: 0.20, damageMult: 1.10 } },

  // ---- Epic (6%) ----
  { id: 'main_char',     name: 'Main Character',   blurb: '+30% Damage, +20% XP',  rarity: 'epic',      category: 'stat', oneTime: true, effect: { damageMult: 1.30, xpMult: 1.20 } },
  { id: 'protein_keg',   name: 'Protein Keg',      blurb: '+60 Max HP, +10% Dmg',  rarity: 'epic',      category: 'stat', oneTime: true, effect: { hpMaxAdd: 60, damageMult: 1.10 } },
  { id: 'phantom_step',  name: 'Phantom Step',     blurb: '+22% Dodge Rate',       rarity: 'epic',      category: 'stat', oneTime: true, effect: { dodgeRate: 0.22 } },

  // ---- Legendary (2%) ----
  { id: 'viral_moment',  name: 'Viral Moment',     blurb: '×2 XP gain permanently',rarity: 'legendary', category: 'stat', oneTime: true, effect: { xpMult: 2.0 } },
  { id: 'transcendence', name: 'Transcendence',    blurb: '+50 HP, +25% Dmg, +15% Movement Speed', rarity: 'legendary', category: 'stat', oneTime: true, effect: { hpMaxAdd: 50, damageMult: 1.25, speedMult: 1.15 } },
];

const RARITY_WEIGHT: Record<Rarity, number> = {
  common: 55,
  uncommon: 25,
  rare: 12,
  epic: 6,
  legendary: 2,
};

export const RARITY_COLOR: Record<Rarity, string> = {
  common:    '#9ca3af',
  uncommon:  '#22c55e',
  rare:      '#3b82f6',
  epic:      '#a855f7',
  legendary: '#f59e0b',
};

export function getPowerup(id: string): Powerup | null {
  return POWERUPS.find((p) => p.id === id) ?? null;
}

// Rarity-weighted random roll, restricted to cards the player is
// allowed to see. Owned oneTime cards are filtered out so a skill
// can only ever be picked once per player. Returns up to `maxCount`
// unique cards. If the pool has fewer eligible entries than
// `maxCount`, returns all of them (callers use this to decide
// whether to show a 2-slot / 1-slot / 0-slot picker).
export function rollChoices(
  role: Role,
  ownedOneTime: ReadonlySet<string>,
  maxCount = 2,
): Powerup[] {
  const eligible = POWERUPS.filter((p) => {
    if (p.faction && p.faction !== role) return false;
    // Once a card is picked it's gone for the rest of the match —
    // regardless of the legacy `oneTime` flag. Prevents duplicates
    // from showing up in any future level-up roll.
    if (ownedOneTime.has(p.id)) return false;
    return true;
  });
  if (eligible.length === 0) return [];

  const picks: Powerup[] = [];
  const remaining = eligible.slice();
  const cap = Math.max(0, Math.min(maxCount, remaining.length));
  for (let i = 0; i < cap && remaining.length > 0; i++) {
    const totalWeight = remaining.reduce((s, p) => s + RARITY_WEIGHT[p.rarity], 0);
    let roll = Math.random() * totalWeight;
    let idx = 0;
    for (let j = 0; j < remaining.length; j++) {
      roll -= RARITY_WEIGHT[remaining[j].rarity];
      if (roll <= 0) { idx = j; break; }
    }
    picks.push(remaining[idx]);
    remaining.splice(idx, 1);
  }
  return picks;
}

// Legacy alias — retained for any carry-over callsite. Delegates to
// rollChoices with the old 3-slot cap so existing behavior is preserved.
export function rollThree(role: Role, ownedOneTime: ReadonlySet<string>): Powerup[] {
  return rollChoices(role, ownedOneTime, 3);
}

// Combined effect of a stack of powerups. Starts from a neutral identity
// and folds all additive/multiplicative fields. Used by both the server
// (HP max / damage / absorption / XP) and the client (speed + body).
export type DerivedStats = {
  hpMaxAdd: number;
  damageMult: number;
  absorptionMult: number;
  xpMult: number;
  speedMult: number;
  circleRadiusMult: number;
  ringOuterMult: number;
  hitboxShrink: number;
  critRate: number;
  critMult: number;
  dodgeRate: number;
};

// Baseline crit + dodge so EVERY player feels combat variance even
// without any powerups picked. Lucky-Strike / Sidestepper / etc all
// stack additively on top of these floors. Tuned low so the base
// can't carry a fight on its own — just adds texture to early-game
// engagements that used to feel deterministic.
const BASE_CRIT_RATE = 0.05;   // 5% — about 1 in 20 hits crits
const BASE_DODGE_RATE = 0.05;  // 5% — about 1 in 20 hits whiff

export function neutralStats(): DerivedStats {
  return {
    hpMaxAdd: 0,
    damageMult: 1,
    absorptionMult: 1,
    xpMult: 1,
    speedMult: 1,
    circleRadiusMult: 1,
    ringOuterMult: 1,
    hitboxShrink: 0,
    critRate: BASE_CRIT_RATE,
    critMult: 1.5,
    dodgeRate: BASE_DODGE_RATE,
  };
}

// 3-phase evolution. Owned powerups grow as the holder levels up.
//   Phase 1 (lvl 1-4)  — base effect.
//   Phase 2 (lvl 5-9)  — +20% on the delta above neutral.
//   Phase 3 (lvl 10+)  — +45% on the delta above neutral. Final form.
// Mythic / legendary `extra` effects are NOT phase-scaled; they're not
// owned powerups and have their own progression.
export type PowerupPhase = 1 | 2 | 3;

export function phaseForLevel(level: number): PowerupPhase {
  if (level >= 10) return 3;
  if (level >= 5) return 2;
  return 1;
}

export function phaseBoost(phase: PowerupPhase): number {
  return phase === 3 ? 1.45 : phase === 2 ? 1.20 : 1.0;
}

// Scale an effect's delta-above-neutral by `boost`. Multiplicative
// fields stay anchored at 1; additive fields scale linearly.
function scaleEffect(e: PowerupEffect, boost: number): PowerupEffect {
  if (boost === 1) return e;
  const out: PowerupEffect = {};
  if (e.hpMaxAdd !== undefined)         out.hpMaxAdd = e.hpMaxAdd * boost;
  if (e.damageMult !== undefined)       out.damageMult = 1 + (e.damageMult - 1) * boost;
  if (e.absorptionMult !== undefined)   out.absorptionMult = 1 + (e.absorptionMult - 1) * boost;
  if (e.xpMult !== undefined)           out.xpMult = 1 + (e.xpMult - 1) * boost;
  if (e.speedMult !== undefined)        out.speedMult = 1 + (e.speedMult - 1) * boost;
  if (e.circleRadiusMult !== undefined) out.circleRadiusMult = 1 + (e.circleRadiusMult - 1) * boost;
  if (e.ringOuterMult !== undefined)    out.ringOuterMult = 1 + (e.ringOuterMult - 1) * boost;
  if (e.hitboxShrink !== undefined)     out.hitboxShrink = Math.min(0.5, e.hitboxShrink * boost);
  if (e.critRate !== undefined)         out.critRate = e.critRate * boost;
  if (e.critMult !== undefined)         out.critMult = 1 + (e.critMult - 1) * boost;
  if (e.dodgeRate !== undefined)        out.dodgeRate = e.dodgeRate * boost;
  return out;
}

export function deriveStats(
  stack: ReadonlyArray<string>,
  extra?: PowerupEffect,
  level: number = 1,
): DerivedStats {
  const s = neutralStats();
  const boost = phaseBoost(phaseForLevel(level));
  const apply = (e: PowerupEffect) => {
    if (e.hpMaxAdd)         s.hpMaxAdd += e.hpMaxAdd;
    if (e.damageMult)       s.damageMult *= e.damageMult;
    if (e.absorptionMult)   s.absorptionMult *= e.absorptionMult;
    if (e.xpMult)           s.xpMult *= e.xpMult;
    if (e.speedMult)        s.speedMult *= e.speedMult;
    if (e.circleRadiusMult) s.circleRadiusMult *= e.circleRadiusMult;
    if (e.ringOuterMult)    s.ringOuterMult *= e.ringOuterMult;
    if (e.hitboxShrink)     s.hitboxShrink = Math.max(s.hitboxShrink, e.hitboxShrink);
    if (e.critRate)         s.critRate += e.critRate;
    // Highest-wins so stacked crit powerups can't chain-multiply past sanity.
    if (e.critMult)         s.critMult = Math.max(s.critMult, e.critMult);
    if (e.dodgeRate)        s.dodgeRate += e.dodgeRate;
  };
  for (const id of stack) {
    const p = getPowerup(id);
    if (p) apply(scaleEffect(p.effect, boost));
  }
  // Mythic / legendary extras stay unscaled — their progression is separate.
  if (extra) apply(extra);
  // Hard caps so stacks don't make players untouchable.
  if (s.critRate > 0.95) s.critRate = 0.95;
  if (s.dodgeRate > 0.6) s.dodgeRate = 0.6;
  return s;
}
